import { useEffect, useRef, useState } from 'react'
import { offerChain } from '@sp/shared'
import { plural } from '../lib/format.ts'
import { chainLabel } from '../lib/offers.ts'
import { Icon } from './Icon.tsx'
import { Button, Checkbox, Chip } from './ui.tsx'

const COUNTRIES = [
  ['CZ', 'Česko'],
  ['DE', 'Německo'],
  ['PL', 'Polsko'],
] as const

/** „Kaufland, Albert", „4 obchody". */
export function chainsLabel(ids: readonly string[]): string {
  if (ids.length === 0) return 'Všechny obchody'
  if (ids.length <= 2) return ids.map((id) => chainLabel(id)).join(', ')
  return plural(ids.length, 'obchod', 'obchody', 'obchodů')
}

/**
 * Vyber obchodu v liste Akci na pocitaci. Jedenact cipu by lista natahla na tri
 * radky, proto rozbalovaci seznam po zemich; „Jen tenhle" necha jeden obchod
 * na jeden klik jako drivejsi cip.
 */
export function ChainPicker({
  chains,
  picked,
  onChange,
}: {
  chains: readonly string[]
  /** Prazdne = vsechny obchody. */
  picked: readonly string[]
  onChange(next: string[]): void
}) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onPointerDown(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const groups = COUNTRIES.map(([country, name]) => ({
    country,
    name,
    ids: chains.filter((id) => (offerChain(id)?.country ?? 'CZ') === country),
  })).filter((group) => group.ids.length > 0)

  function toggle(id: string) {
    onChange(picked.includes(id) ? picked.filter((x) => x !== id) : [...picked, id])
  }

  return (
    <div ref={rootRef} className="chain-picker">
      <Chip
        className="chip-sm"
        active={picked.length > 0}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name="store" size={16} />
        {chainsLabel(picked)}
        <Icon name={open ? 'chevron_up' : 'chevron_down'} size={16} />
      </Chip>
      {open && (
        <div className="chain-menu" role="group" aria-label="Obchody">
          <div className="chain-menu-head">
            <span className="field-label">Obchody</span>
            {picked.length > 0 && (
              <Button kind="text" compact onClick={() => onChange([])}>
                Všechny
              </Button>
            )}
          </div>
          {groups.map((group) => (
            <div key={group.country} className="chain-menu-group">
              {groups.length > 1 && <span className="chain-menu-country">{group.name}</span>}
              {group.ids.map((id) => {
                const on = picked.includes(id)
                return (
                  <div key={id} className="chain-menu-row">
                    <button
                      type="button"
                      role="checkbox"
                      aria-checked={on}
                      className="chain-menu-toggle"
                      onClick={() => toggle(id)}
                    >
                      <Checkbox checked={on} />
                      <span>{chainLabel(id)}</span>
                    </button>
                    <button
                      type="button"
                      className="chain-menu-only"
                      aria-label={`Jen ${chainLabel(id)}`}
                      onClick={() => {
                        onChange([id])
                        setOpen(false)
                      }}
                    >
                      Jen tenhle
                    </button>
                  </div>
                )
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
